/**
 * @fileoverview Condition rows ↔ one column's filter group
 *
 * The editor holds a chain of rows, each joined to the previous one by AND or OR. AND binds
 * tighter than OR, as in SQL, so the group is an OR of AND-terms: `A OR B AND C` is
 * `{OR, [a, {AND, [b, c]}]}`. A single term needs no OR level: `A AND B` is `{AND, [a, b]}`.
 *
 * Between is the only row that is not one condition: it is its own `{AND, [GTE, LTE]}` group,
 * flat only when it is the whole filter (the shape saved before chains). Placed flat inside an
 * OR, its two bounds would each match on their own.
 */

import type { ColFilter, ColFilterGroup, ColFilterNode } from "../filterModel";
import { FilterOperator, isColFilterGroup, LogicalOperator } from "../filterModel";
import type { ConditionFilterKind } from "../types";
import { isValuelessOperator } from "./operators";
import type { RangeTermCompare } from "./rangeOrder";
import { isInvertedRange } from "./rangeOrder";

export interface ConditionRow {
  operator: FilterOperator;
  term: string;
  /** Second bound, read only for `IN_RANGE`. */
  termTo: string;
  /** Links the row to the previous one; ignored on the first row. */
  join: LogicalOperator;
}

export interface ParseConditionGroupOptions {
  /** Dates and numbers read a GTE + LTE pair as one row; only dates read an LT + GT pair. */
  kind: ConditionFilterKind;
  /** Stands in for a saved condition without an operator. */
  defaultOperator: FilterOperator;
  /** Re-encodes a saved term, e.g. a date at the column's own shape; "" drops the row. */
  normalizeTerm?: (term: string) => string;
}

export function emptyConditionRow(operator: FilterOperator, join: LogicalOperator): ConditionRow {
  return { operator, term: "", termTo: "", join };
}

/**
 * Only complete rows filter: a valueless operator, or every term the operator asks for. With
 * `compare`, a Between whose From lies after its To is incomplete too.
 */
export function isConditionRowComplete(row: ConditionRow, compare?: RangeTermCompare): boolean {
  if (isValuelessOperator(row.operator)) return true;
  if (row.operator === FilterOperator.IN_RANGE) {
    return row.term !== "" && row.termTo !== "" && !(compare && isInvertedRange(row, compare));
  }
  return row.term !== "";
}

/** What a filter is made of: the complete rows, the first one unjoined. */
export function completeConditionRows(rows: readonly ConditionRow[], compare?: RangeTermCompare): ConditionRow[] {
  return rows
    .filter((row) => isConditionRowComplete(row, compare))
    .map((row, index) =>
      index === 0 && row.join !== LogicalOperator.AND ? { ...row, join: LogicalOperator.AND } : row,
    );
}

/** The column's group for the complete rows, or null when none is complete. */
export function buildConditionGroup(colKey: string, rows: readonly ConditionRow[]): ColFilterGroup | null {
  const terms: ConditionRow[][] = [];
  for (const row of rows.filter((candidate) => isConditionRowComplete(candidate))) {
    const current = terms.at(-1);
    if (current && row.join === LogicalOperator.AND) current.push(row);
    else terms.push([row]);
  }
  if (terms.length === 0) return null;

  const group = (logicalOperator: LogicalOperator, conditions: ColFilterNode[]): ColFilterGroup => ({
    colKey,
    logicalOperator,
    conditions,
  });
  const leaf = (operator: FilterOperator, filterTerm: string): ColFilter => ({ colKey, filterTerm, operator });
  const rowNode = (row: ConditionRow): ColFilterNode => {
    if (row.operator === FilterOperator.IN_RANGE) {
      return group(LogicalOperator.AND, [
        leaf(FilterOperator.GREATER_OR_EQUALS, row.term),
        leaf(FilterOperator.LESS_OR_EQUALS, row.termTo),
      ]);
    }
    return leaf(row.operator, isValuelessOperator(row.operator) ? "" : row.term);
  };
  const termNode = (term: ConditionRow[]): ColFilterNode =>
    term.length === 1 ? rowNode(term[0]) : group(LogicalOperator.AND, term.map(rowNode));

  if (terms.length > 1) return group(LogicalOperator.OR, terms.map(termNode));
  const only = termNode(terms[0]);
  return isColFilterGroup(only) ? only : group(LogicalOperator.AND, [only]);
}

/**
 * The rows of a column's group: the inverse of {@link buildConditionGroup}, plus the shapes saved
 * before chains — a GTE + LTE pair is one Between (on dates, Equals when both ends name the same
 * period), and on dates an OR of LT + GT naming one period is the old day-bound Not equals. Shapes
 * the builder never writes are flattened depth-first, each child joined by its nearest group's
 * operator.
 */
export function parseConditionGroup(
  group: ColFilterGroup | null | undefined,
  options: ParseConditionGroupOptions,
): ConditionRow[] {
  return group ? parseNode(group, LogicalOperator.AND, options) : [];
}

function parseNode(node: ColFilterNode, join: LogicalOperator, options: ParseConditionGroupOptions): ConditionRow[] {
  if (!isColFilterGroup(node)) return [leafRow(node, join, options)];

  const pair = pairRow(node, join, options);
  if (pair) return [pair];

  const rows: ConditionRow[] = [];
  for (const child of node.conditions) {
    rows.push(...parseNode(child, rows.length === 0 ? join : node.logicalOperator, options));
  }
  return rows;
}

function leafRow(condition: ColFilter, join: LogicalOperator, options: ParseConditionGroupOptions): ConditionRow {
  const operator = (condition.operator as FilterOperator | undefined) ?? options.defaultOperator;
  const term = isValuelessOperator(operator) ? "" : normalize(condition, options);
  return { operator, term, termTo: "", join };
}

function readsRangePairs(kind: ConditionFilterKind): boolean {
  return kind === "date" || kind === "number";
}

function pairRow(
  group: ColFilterGroup,
  join: LogicalOperator,
  options: ParseConditionGroupOptions,
): ConditionRow | null {
  if (group.conditions.length !== 2) return null;
  const leaves = group.conditions.filter((node): node is ColFilter => !isColFilterGroup(node));
  const byOperator = (operator: FilterOperator) => leaves.find((leaf) => leaf.operator === operator);

  if (group.logicalOperator === LogicalOperator.AND) {
    return readsRangePairs(options.kind) ? rangePairRow(byOperator, join, options) : null;
  }
  return options.kind === "date" ? dateNotEqualsRow(byOperator, join, options) : null;
}

function rangePairRow(
  byOperator: (operator: FilterOperator) => ColFilter | undefined,
  join: LogicalOperator,
  options: ParseConditionGroupOptions,
): ConditionRow | null {
  const ge = byOperator(FilterOperator.GREATER_OR_EQUALS);
  const le = byOperator(FilterOperator.LESS_OR_EQUALS);
  if (!ge || !le) return null;
  const start = normalize(ge, options);
  const end = normalize(le, options);
  if (options.kind === "date" && start && start === end) {
    return { operator: FilterOperator.EQUALS, term: start, termTo: "", join };
  }
  return { operator: FilterOperator.IN_RANGE, term: start, termTo: end, join };
}

function dateNotEqualsRow(
  byOperator: (operator: FilterOperator) => ColFilter | undefined,
  join: LogicalOperator,
  options: ParseConditionGroupOptions,
): ConditionRow | null {
  const lt = byOperator(FilterOperator.LESS_THAN);
  const gt = byOperator(FilterOperator.GREATER_THAN);
  if (!lt || !gt) return null;
  const term = normalize(gt, options);
  return term && term === normalize(lt, options)
    ? { operator: FilterOperator.NOT_EQUALS, term, termTo: "", join }
    : null;
}

function normalize(condition: ColFilter, options: ParseConditionGroupOptions): string {
  const term = condition.filterTerm ?? "";
  return options.normalizeTerm ? options.normalizeTerm(term) : term;
}
